import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Spin, Tooltip } from "antd";
import { MenuFoldOutlined, MenuUnfoldOutlined } from "@ant-design/icons";

import {
  MasksFiltersContextProvider,
  WebViewerProvider,
  useMasksContext,
  useMessageContext,
  useModalContext,
  useTaskContext,
} from "../../providers";

import { PATH_ROUTES } from "../../router/routes";

import { Task } from "../../types/task";

import CustomWebViewer from "./CustomWebViewer";

import FiltersMasksModal from "../../components/Modals/FiltersMasks/FiltersMasks";
import DeleteMassiveOccurrences from "../../components/Modals/DeleteMassiveOccurrences/DeleteMassiveOccurrences";
import ValidateTaskPopup from "../../components/Modals/ValidateTask/ValidateTask";

import styles from "./DocumentsViewer.module.css";
import { HomeOutlined } from "@ant-design/icons";
import ValidationTag from "../../components/Tag/ValidationTag";
import RightAside from "./RightAside/RightAside";


const DocumentsViewer = () => {
  const navigate = useNavigate();
  const [isRightAsideOpen, setIsRightAsideOpen] = useState(false);

  const { task, isLoading, error } = useTaskContext();
  const { getApiMasks, masks } = useMasksContext();
  const { sendMessage } = useMessageContext();
  const { docSelected, setDocument, setTask, openValidateTask } = useModalContext();


  const previousTask = useRef<Task | null>(null);

  useEffect(() => {
    const onHandleInitialData = async (task: Task) => {
      const targetDocSelected = docSelected || task.documents.RPP_JT[0].RPP;
      setDocument(targetDocSelected);
      setTask(task);
      if (targetDocSelected !== docSelected) {
        await getApiMasks(task.id, targetDocSelected?.id as string);
      }
      task.deleted_at !== null &&
        sendMessage(
          "This task has been deleted. You cannot perform any actions on it.",
          "info",
          3.5
        );
    };

    if (task) {
      previousTask.current = task;
      onHandleInitialData(task);
    }

    return () => setDocument(null);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task]);

  if (isLoading) return <Spin tip="Carregant tasca..." spinning={isLoading} fullscreen />;

  if (error) {
    if (error.statusCode === 400) sendMessage("Tasca invàlida", "error", 2.5);
    if (error.statusCode === 404) sendMessage("Tasca no trobada", "error", 2.5);
    if (error.statusCode === 422) sendMessage("Id invàlid", "error", 2.5);
    navigate(PATH_ROUTES.home);
    return null;
  }

  return (
    <WebViewerProvider>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.leftSection}>
            <Button onClick={() => navigate("/")} icon={<HomeOutlined style={{ fontSize: "14px" }} />} />
            <div className={styles.separator}></div>
            <h1 className={styles.taskTitle}>
              {task?.name}
            </h1>
            <div className={styles.separator}></div>
            <span>{task?.doc_name}</span>
            <ValidationTag
              validated_at={task?.validated_at ?? ""}
            />
          </div>
          <div className={styles.rightSection}>
            {task?.validated_at ? null : 
            <Button
              onClick={() => {
                task && setTask(task);
                openValidateTask();
              }}
              size="large"
            >
              Validar tasca
            </Button>}
            <Tooltip title={isRightAsideOpen ? "Ocultar llista de màscares" : "Mostrar llista de màscares"}>
              <Button
                onClick={() => setIsRightAsideOpen(!isRightAsideOpen)}
                icon={isRightAsideOpen ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                size="large"
                type={"default"}
              />
            </Tooltip>
          </div>
        </div>
        <div className={styles.contentContainer}>
          {docSelected && <CustomWebViewer docSelected={docSelected} />}
          <MasksFiltersContextProvider>
            <FiltersMasksModal />
            {isRightAsideOpen && (
              <RightAside
                masks={masks}
                masksNotifications={[]}
              />
            )}
          </MasksFiltersContextProvider>
        </div>
      </div>
      <DeleteMassiveOccurrences />
      <ValidateTaskPopup />
    </WebViewerProvider>
  );
};
export default DocumentsViewer;
